import { doc, collection, setDoc, updateDoc } from "firebase/firestore";
import { db } from "./firebase";
import urls from "./urls";


let client, accessToken;

const getAccessToken = () => accessToken;

const refreshAccessToken = async user => {
    console.log("Access token expired, requesting token refresh");
    let response = await fetch(urls.googleAuthToken, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userId: user.id,
            refresh: true
        })
    });
    
    try {
        accessToken = (await response.json()).accessToken;
        console.log("Refreshed token:", accessToken);
        return accessToken;
    } catch (err) {
        console.warn(`Error refreshing token: `, err);
        return false;
    }
};

const authenticateClassroomAPI = async user => {
    let response = await fetch(urls.googleAuthToken, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userId: user.id
        })
    });
    
    accessToken = (await response.json()).accessToken;
    
    if (!accessToken && client) {
        await client.requestCode();
        return true;
    } 

    let testResponse = await fetch('https://classroom.googleapis.com/v1/courses', { headers: { 'Authorization': `Bearer ${accessToken}` } });
    let success = testResponse.ok && testResponse.status !== 401;
    if (!success)
        success = await refreshAccessToken(user);
        
    localStorage.setItem("loggedInClassroom", success);
    return success ? accessToken : false;
};

const isLoggedIn = async user => {
    let response = await fetch(urls.googleAuthToken, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            userId: user.id
        })
    });
    
    accessToken = (await response.json()).accessToken;
    return localStorage.getItem("loggedInClassroom") === "true" && accessToken !== undefined;
}

const loadClassroomAPI = async (user, callback) => {
    if (!user)
        return;

    const GOOGLE_CLASSROOM_CLIENT_ID = '987817055798-opkqie2cphqg7cpci9a24bo1bd2f5rlr.apps.googleusercontent.com';
    // const GOOGLE_CLASSROOM_API_KEY = 'AIzaSyC2vhNpYoas6NBBoTa8Ac3tPu_H2vQKE_I';
    // const DISCOVERY_DOCS = ["https://classroom.googleapis.com/$discovery/rest"];
    const SCOPES = "https://www.googleapis.com/auth/classroom.courses.readonly https://www.googleapis.com/auth/classroom.coursework.me";

    const loadScript = async (src, callback) => {
        let script = document.createElement('script');
        script.type = 'text/javascript';
        script.src = src;

        return new Promise(async (resolve, reject) => {
            script.onload = async () => {
                try {
                    await callback();
                    resolve();
                } catch (err) {
                    console.warn(`<Settings> Error loading script ${src}: ${err}`);
                    reject(err);
                } 
            };

            document.getElementsByTagName('head')[0].appendChild(script);
        });
    }

    if (!window.google?.accounts?.oauth2) {
        try {
            await loadScript('https://accounts.google.com/gsi/client', async () => {
                console.log('<Classroom API> Loaded gsi');
                client = window.google.accounts.oauth2.initCodeClient({
                    client_id: GOOGLE_CLASSROOM_CLIENT_ID,
                    scope: SCOPES,
                    access_type: 'offline',
                    ux_mode: 'popup',
                    callback: async tokenResponse => {
                        if (tokenResponse.error)
                            return;
                        
                        let response = await fetch(urls.googleAuthToken, {
                            method: 'POST',
                            headers: {
                                'Content-Type': 'application/json',
                            },
                            body: JSON.stringify({
                                code: tokenResponse.code,
                                userId: user.id
                            })
                        });
                        
                        let { tokens } = await response.json();
                        accessToken = tokens.access_token;
                        localStorage.setItem("loggedInClassroom", true);
                        callback?.(accessToken)
                    },
                });             
            });
        } catch (err) {
            console.warn(`Couldn't load gsi: ${err}`);
        }   
    };
};

const fetchEvents = async (offline, homeworks, setHomeworks, timetable, user) => {
    if (offline || !homeworks || !accessToken)
        return [false, false];

    const newHomeworks = { ...homeworks };
    const classroomHomeworks = [];
    let newEvents = false, updatedEvents = false;
    Object.values(homeworks).forEach(homework => {
        if (homework.classroomId) 
            classroomHomeworks.push(homework);
    });
    
    let response = await fetch('https://classroom.googleapis.com/v1/courses', { headers: { 'Authorization': `Bearer ${accessToken}` } });
    if (!response.ok && response.status === 401) {
        await refreshAccessToken(user);
        response = await fetch('https://classroom.googleapis.com/v1/courses', { headers: { 'Authorization': `Bearer ${accessToken}` } });
    }

    const courses = (await response.json()).courses;
    if (!courses)
        return [false, false];

    const coursesRequests = Object.values(courses).map(course => {
        return new Promise(async (resolve) => {
            try {            
                response = await fetch(`https://classroom.googleapis.com/v1/courses/${course.id}/courseWork`, { headers: { 'Authorization': `Bearer ${accessToken}` } });

                const courseWorks = (await response.json()).courseWork;
                let subject;
                if (user?.classroomSubjects?.[course.id])
                    subject = user.classroomSubjects[course.id];
                /*else if (user?.classroomSubjects?.[course.id] !== false)
                    Object.values(data.subjects).forEach(classSubject => {
                        if (classSubject.subject.toLowerCase().includes(course.name.toLowerCase()) || course.name.toLowerCase().includes(classSubject.subject.toLowerCase())) 
                            subject = classSubject;
                    });*/
                if (subject && courseWorks) {
                    const courseWorksRequests = Object.values(courseWorks).map(courseWork => {
                        return new Promise(async (resolve) => {
                            if (courseWork.dueDate) {
                                let dueDate = new Date(courseWork.dueDate.year, courseWork.dueDate.month-1, courseWork.dueDate.day);
                                let lesson;
                                Object.values(timetable[dueDate.getDay()]).forEach(dayClass => {
                                    if (dayClass.subjectTitle === subject) 
                                        lesson = dayClass;
                                });
                                
                                if (lesson) {
                                    dueDate.setUTCHours(lesson.startTime.split(":")[0]);
                                    dueDate.setUTCMinutes(lesson.startTime.split(":")[1]);
                                    dueDate.setSeconds(0);
                                    dueDate.setMilliseconds(0);
                                    dueDate = dueDate.addDays(1);
                                } else {
                                    dueDate.setUTCHours(8);
                                    dueDate.setUTCMinutes(0);
                                    dueDate.setSeconds(0);
                                    dueDate.setMilliseconds(0);
                                    dueDate = dueDate.addDays(1);
                                }
                                
                                if (dueDate.getTime() >= new Date().addDays(-7).getTime()) {
                                    response = await fetch(`https://classroom.googleapis.com/v1/courses/${course.id}/courseWork/${courseWork.id}/studentSubmissions`, { headers: { 'Authorization': `Bearer ${accessToken}` } });
                                    const studentSubmissions = (await response.json()).studentSubmissions;
                                    const done = ["TURNED_IN", "RETURNED"].includes(studentSubmissions[0].state);
                                    const teacherGrade = studentSubmissions[0].assignedGrade;
                                    let grade;
                                    if (typeof teacherGrade != 'undefined' && typeof courseWork.maxPoints != 'undefined') 
                                        grade = Number(parseFloat(teacherGrade) / (parseFloat(courseWork.maxPoints) / 10)).toFixed(1);

                                    let alreadyAdded = false;
                                    Object.values(homeworks).forEach(homework => {
                                        if (homework.classroomId === courseWork.id) alreadyAdded = homework;
                                    });
                                    
                                    if (alreadyAdded === false) {
                                        const docRef = doc(collection(db, `schools/${user.school}/classes/${user.class}/homeworks`));

                                        const newEvent = {
                                            id: docRef.id,
                                            subject,
                                            type: 'homework',
                                            title: `${courseWork.title}${courseWork.description ? "\n" + courseWork.description : ""}`,
                                            addedBy: user.id,
                                            done: {
                                                [user.id]: done,
                                            },
                                            for: [user.id],
                                            classroomId: courseWork.id,
                                            classroomURL: courseWork.alternateLink,
                                            date: dueDate.toString(),
                                            grades: { [user.id]: { inGradeAverage: false } },
                                        };
                                        if (grade)
                                            newEvent.grades[user.id].grade = grade;

                                        await setDoc(doc(db, `schools/${user.school}/classes/${user.class}/homeworks`, docRef.id), newEvent);
                                        newHomeworks[newEvent.id] = newEvent;
                                        
                                        newEvents = true;
                                        resolve();
                                    } else if (alreadyAdded) {                                   
                                        if (alreadyAdded.for?.includes(user.id)) {
                                            if (parseFloat(grade) !== parseFloat(alreadyAdded.grades?.[user.id]?.grade) && grade) {
                                                const newEvent = {
                                                    ...alreadyAdded,
                                                    subject,
                                                    title: `${courseWork.title}${courseWork.description ? "\n" + courseWork.description : ""}`,
                                                    done: { ...alreadyAdded.done, [user.id]: done },
                                                    classroomId: courseWork.id,
                                                    classroomURL: courseWork.alternateLink,
                                                    grades: {
                                                        ...alreadyAdded.grades,
                                                        [user.id]: { grade, inGradeAverage: false },
                                                    }
                                                };
                                                
                                                await setDoc(doc(db, `schools/${user.school}/classes/${user.class}/homeworks`, alreadyAdded.id), newEvent);
                                                newHomeworks[newEvent.id] = newEvent;
                                                updatedEvents = true;
                                            } else {
                                                if (alreadyAdded.done[user.id] === done) resolve();
                                                else {
                                                    await updateDoc(doc(db, `schools/${user.school}/classes/${user.class}/homeworks`, alreadyAdded.id), {
                                                        ["done." + user.id]: done,
                                                    });
                                                }
                                            }
                                        } else {
                                            let eventFor = alreadyAdded.for ?? [user.id];
                                            if (!eventFor.includes(user.id)) eventFor.push(user.id);
                                            const newEvent = {
                                                ...alreadyAdded,
                                                for: eventFor,
                                                subject,
                                                title: `${courseWork.title}${courseWork.description ? "\n" + courseWork.description : ""}`,
                                                classroomId: courseWork.id,
                                                classroomURL: courseWork.alternateLink,
                                            };
                                            if (typeof grade != 'undefined')
                                                newEvent.grades[user.id] = { grade, inGradeAverage: false };
                                                
                                            await setDoc(doc(db, `schools/${user.school}/classes/${user.class}/homeworks`, alreadyAdded.id), newEvent);
                                            newHomeworks[newEvent.id] = newEvent;
                                            newEvents = true;
                                        }

                                        resolve();
                                    } else resolve();
                                } else resolve();
                            } else resolve();
                        });
                    });
                    await Promise.all(courseWorksRequests);
                    resolve();
                } else {
                    resolve();
                }
            } catch (err) {
                console.warn(`Error with classroom course ${course}: ${err}`)
                resolve();
            }
        });
    });

    await Promise.all(coursesRequests);

    if (newEvents || updatedEvents) 
        setHomeworks({ ...newHomeworks });

    return [newEvents, updatedEvents];
};

const revokeToken = async user => {    
    const response = await fetch(urls.googleAuthTokenRevoke, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json'
        },
        body: JSON.stringify({
            accessToken,
            userId: user.id
        })
    });

    if (response.ok) console.log("Access token revoked");
    else console.warn("Error trying to revoke access token:", response);

    // Has to be false if request is successfull
    return !response.ok;
};

const getCourses = async () => {
    let courses = null;

    try {
        let response = await fetch('https://classroom.googleapis.com/v1/courses', { headers: { 'Authorization': `Bearer ${accessToken}` } });
        courses = (await response.json()).courses;
    } catch (err) { 
        console.warn("Error retrieving classroom courses:", err);
    }
    
    return courses;
};

export {
    getAccessToken,
    loadClassroomAPI,
    isLoggedIn,
    authenticateClassroomAPI,
    fetchEvents,
    revokeToken,
    getCourses,
};